package b2d

import b2d.userdata.*
import com.soywiz.kds.iterators.*
import com.soywiz.korge.box2d.*
import com.soywiz.korge.view.*
import com.soywiz.korge.view.tiles.*
import com.soywiz.korim.color.*
import com.soywiz.korim.tiles.tiled.*
import com.soywiz.korio.file.std.*
import com.soywiz.korma.geom.*
import component.*
import game.*
import org.jbox2d.collision.shapes.*
import org.jbox2d.common.*
import org.jbox2d.dynamics.*
import utils.*

object WorldObjectFactory {

    const val WATER_TILE = "water_tile"
    const val ITEMS_TILE = "items_tile"
    const val TRAPS_TILE = "traps_tile"
    const val GROUND_TILE = "ground_tile"
    const val CAVE_TILE = "cave_tile"

    const val GROUND_LAYER = "ground"
    const val WATER_LAYER = "water_area"
    const val SPAWN_AREA = "spawn_area"
    const val FINISH_AREA = "finish_area"
    const val ABILITY_SELECTION = "ability_selection_area"
    const val TRAPS = "traps"
    const val COINS = "coins"
    const val TILE_SCALE = 1.0

    suspend fun loadLevel(
        world: World,
        worldLayers: WorldLayers,
        levelFile: String
    ): WorldMetaData {
        val tiledMap = resourcesVfs[levelFile].readTiledMap()
        // sky
//        val skyWidth = tiledMap.width * tiledMap.tilewidth * TILE_SCALE
//        val skyHeight = tiledMap.height * tiledMap.tileheight * TILE_SCALE
//        worldLayers.ground.solidRect(
//            width = skyWidth * 1.1,
//            height = skyHeight * 1.1,
//            RGBA(37,197,223)
//        ).position(-((skyWidth * 1.1 - skyWidth) / 2.0), 0.0)

        val objectLayers = tiledMap.objectLayers.associateBy { it.name }
        // load ground
        objectLayers[GROUND_LAYER]!!.objects.fastForEach { groundObject ->
            when (groundObject.objectShape) {
                is TiledMap.Object.Shape.Rectangle -> {
                    val body = buildBoxGround(groundObject, TILE_SCALE, world)
                    groundObject.objectShape
                    println(body.position)
                }

                is TiledMap.Object.Shape.Polygon -> {
                    val body = buildPolygonGround(groundObject, TILE_SCALE, world)
                    groundObject.objectShape
                    println(body.position)
                }

                else -> {
                    println("unknown ground object shape ${groundObject.objectShape::class.simpleName}")
                }
            }
        }

//        val firstAbilityPointObject = objectLayers[ABILITY_SELECTION]!!.objects[0]
//        val firstAbilityPoint = Point(
//            (firstAbilityPointObject.x * TILE_SCALE) / world.customScale,
//            (firstAbilityPointObject.y * TILE_SCALE) / world.customScale
//        )
//
//        val secondAbilityPointObject = objectLayers[ABILITY_SELECTION]!!.objects[1]
//        val secondAbilityPoint = Point(
//            (secondAbilityPointObject.x * TILE_SCALE) / world.customScale,
//            (secondAbilityPointObject.y * TILE_SCALE) / world.customScale
//        )

        // load spawn area
        val spawnAreaObject = objectLayers[SPAWN_AREA]!!.objects.first()
        val spawnRect = worldLayers.ground.buildSpawnArea(spawnAreaObject, TILE_SCALE, world)

        // load finish area
        val finishAreaObject = objectLayers[FINISH_AREA]!!.objects.first()
        val finishRect = worldLayers.ground.buildFinishArea(finishAreaObject, TILE_SCALE, world)

        val traps = objectLayers[TRAPS]!!.objects.map { trapObject ->
            buildTrap(trapObject, worldLayers, TILE_SCALE, world)
        }

        val coins = objectLayers[COINS]!!.objects.map { coinObject ->
            buildCoin(coinObject, worldLayers, TILE_SCALE, world)
        }

        // load water areas
        objectLayers[WATER_LAYER]?.objects?.fastForEach { groundObject ->
            when (groundObject.objectShape) {
                is TiledMap.Object.Shape.Rectangle -> {
                    val body = buildBoxWater(groundObject, TILE_SCALE, world)
                    groundObject.objectShape
                    println(body.position)
                }

                is TiledMap.Object.Shape.Polygon -> {
                    TODO("Not implemented yet")
//                    val body = buildPolygonGround(groundObject, TILE_SCALE, world)
//                    groundObject.objectShape
//                    println(body.position)
                }

                else -> {
                    println("unknown ground object shape ${groundObject.objectShape::class.simpleName}")
                }
            }
        }

//        worldLayers.ground.tiledMapView(tiledMap, showShapes = false) {
//            scale = TILE_SCALE
//        }

        val tileset = tiledMap.tilesets.toTileSet()
        tiledMap.tileLayers.fastForEach {
            when (it.name) {
                WATER_TILE -> {
                    worldLayers.water.tileMap(
                        map = it.map,
                        tileset = tileset,
                        smoothing = true,
                        orientation = tiledMap.data.orientation,
                        staggerAxis = tiledMap.data.staggerAxis,
                        staggerIndex = tiledMap.data.staggerIndex,
                        tileSize = Size(tiledMap.tilewidth.toDouble(), tiledMap.tileheight.toDouble()),
                    )
                }

                GROUND_TILE -> {
                    worldLayers.ground.tileMap(
                        map = it.map,
                        tileset = tileset,
                        smoothing = true,
                        orientation = tiledMap.data.orientation,
                        staggerAxis = tiledMap.data.staggerAxis,
                        staggerIndex = tiledMap.data.staggerIndex,
                        tileSize = Size(tiledMap.tilewidth.toDouble(), tiledMap.tileheight.toDouble()),
                    )
                }

                TRAPS_TILE -> {
                    worldLayers.traps.tileMap(
                        map = it.map,
                        tileset = tileset,
                        smoothing = true,
                        orientation = tiledMap.data.orientation,
                        staggerAxis = tiledMap.data.staggerAxis,
                        staggerIndex = tiledMap.data.staggerIndex,
                        tileSize = Size(tiledMap.tilewidth.toDouble(), tiledMap.tileheight.toDouble()),
                    )
                }

                CAVE_TILE -> {
                    worldLayers.cave.tileMap(
                        map = it.map,
                        tileset = tileset,
                        smoothing = true,
                        orientation = tiledMap.data.orientation,
                        staggerAxis = tiledMap.data.staggerAxis,
                        staggerIndex = tiledMap.data.staggerIndex,
                        tileSize = Size(tiledMap.tilewidth.toDouble(), tiledMap.tileheight.toDouble()),
                    )
                }

                ITEMS_TILE -> {
                    worldLayers.items.tileMap(
                        map = it.map,
                        tileset = tileset,
                        smoothing = true,
                        orientation = tiledMap.data.orientation,
                        staggerAxis = tiledMap.data.staggerAxis,
                        staggerIndex = tiledMap.data.staggerIndex,
                        tileSize = Size(tiledMap.tilewidth.toDouble(), tiledMap.tileheight.toDouble()),
                    )
                }

                else -> {}
            }
        }

        return WorldMetaData(
            spawnArea = spawnRect,
            finishArea = finishRect,
            traps = traps,
            coins = coins
        )
    }

    fun buildBoxGround(
        groundObject: TiledMap.Object,
        tileScale: Double = 1.0,
        world: World
    ): Body {
        val posX = (groundObject.x * tileScale) / world.customScale
        val posY = (groundObject.y * tileScale) / world.customScale
//            val bounds = (groundObject.bounds * TILE_SCALE) / world.customScale
        val bounds = groundObject.bounds * tileScale

        val body = world.createBody(BodyDef().apply {
            type = BodyType.STATIC
            position.set(posX, posY)
        })

        val groundBoxWidth = bounds.width / world.customScale
        val groundBoxHeight = bounds.height / world.customScale

        body.createFixture(FixtureDef().apply {
            // x и y должны быть 0, потому что рисуется относительно позиции
            // для объектов это верхний левый угол
            shape = BoxShape(groundBoxWidth, groundBoxHeight)
            restitution = 0f
            friction = 0.5f
        })

        // left wall sensor
        val wallDepthMargin = PlayerBuilder.PLAYER_RADIUS / 12.0 / world.customScale
        val wallHeigthMargin = PlayerBuilder.PLAYER_RADIUS / 10.0 / world.customScale
        body.createFixture(FixtureDef().apply {
            shape = PolygonShape().apply {
                count = 4
                count = 4
                vertices[0].set(-wallDepthMargin, wallHeigthMargin)
                vertices[1].set(wallDepthMargin, wallHeigthMargin)
                vertices[2].set(wallDepthMargin, groundBoxHeight - wallHeigthMargin)
                vertices[3].set(-wallDepthMargin, groundBoxHeight - wallHeigthMargin)
                normals[0].set(0.0f, -1.0f)
                normals[1].set(1.0f, 0.0f)
                normals[2].set(0.0f, 1.0f)
                normals[3].set(-1.0f, 0.0f)
                centroid.setZero()
            }
            isSensor = true
            filter = Filter().apply {
                categoryBits = B2dParameters.WALL_LEFT_BIT
                maskBits = B2dParameters.PLAYER_BIT
            }
        })
        // right wall sensor
        body.createFixture(FixtureDef().apply {
            shape = PolygonShape().apply {
                count = 4
                vertices[0].set(groundBoxWidth - wallDepthMargin, wallHeigthMargin)
                vertices[1].set(groundBoxWidth + wallDepthMargin, wallHeigthMargin)
                vertices[2].set(groundBoxWidth + wallDepthMargin, groundBoxHeight - wallHeigthMargin)
                vertices[3].set(groundBoxWidth - wallDepthMargin, groundBoxHeight - wallHeigthMargin)
                normals[0].set(0.0f, -1.0f)
                normals[1].set(1.0f, 0.0f)
                normals[2].set(0.0f, 1.0f)
                normals[3].set(-1.0f, 0.0f)
                centroid.setZero()
            }
            isSensor = true
            filter = Filter().apply {
                categoryBits = B2dParameters.WALL_RIGHT_BIT
                maskBits = B2dParameters.PLAYER_BIT
            }
        })

        println((body.m_fixtureList?.m_shape as PolygonShape).vertices.joinToString { it.toString() })
        println("${body.position.x}, ${body.position.y}")

        return body
    }

    fun buildPolygonGround(
        groundObject: TiledMap.Object,
        tileScale: Double = 1.0,
        world: World
    ): Body {
        val posX = (groundObject.x * tileScale) / world.customScale
        val posY = (groundObject.y * tileScale) / world.customScale

        val objectShape = groundObject.objectShape as TiledMap.Object.Shape.Polygon

        val body = world.createBody(BodyDef().apply {
            type = BodyType.STATIC
            position.set(posX, posY)
        })
        body.createFixture(FixtureDef().apply {
            // x и y должны быть 0, потому что рисуется относительно позиции
            // для объектов это верхний левый угол
            shape = PolygonShape().apply {
                set(objectShape.points
                    .map {
                        Vec2(
                            (it.x / world.customScale).toFloat(),
                            (it.y / world.customScale).toFloat()
                        )
                    }
                    .toTypedArray(),
                    objectShape.points.size)
            }
            friction = 0.2f
            restitution = 0f
        })
        println((body.m_fixtureList?.m_shape as PolygonShape).vertices.joinToString { it.toString() })
        println("${body.position.x}, ${body.position.y}")

        return body
    }

    fun buildBoxWater(
        groundObject: TiledMap.Object,
        tileScale: Double = 1.0,
        world: World
    ): Body {
        val posX = (groundObject.x * tileScale) / world.customScale
        val posY = (groundObject.y * tileScale) / world.customScale
//            val bounds = (groundObject.bounds * TILE_SCALE) / world.customScale
        val bounds = groundObject.bounds * TILE_SCALE

        val body = world.createBody(BodyDef().apply {
            type = BodyType.STATIC
            position.set(posX, posY)
        })
        body.createFixture(FixtureDef().apply {
            // x и y должны быть 0, потому что рисуется относительно позиции
            // для объектов это верхний левый угол
            shape = BoxShape(bounds.width / world.customScale, bounds.height / world.customScale)
            friction = 0.2f
            isSensor = true
            userData = Water()
            filter = Filter().apply {
                categoryBits = B2dParameters.WATER_BIT
                maskBits = B2dParameters.PLAYER_BIT
            }
        })
        println((body.m_fixtureList?.m_shape as PolygonShape).vertices.joinToString { it.toString() })
        println("${body.position.x}, ${body.position.y}")

        return body
    }

    fun Container.buildSpawnArea(
        groundObject: TiledMap.Object,
        tileScale: Double = 1.0,
        world: World
    ): View {
        return this.solidRect(
            groundObject.bounds.width * tileScale,
            groundObject.bounds.height * tileScale,
            Colors.TRANSPARENT_BLACK
        ).position(groundObject.x * tileScale, groundObject.y * tileScale)
    }

    fun Container.buildFinishArea(
        groundObject: TiledMap.Object,
        tileScale: Double = 1.0,
        world: World
    ): View {
        return this.solidRect(
            groundObject.bounds.width * tileScale,
            groundObject.bounds.height * tileScale,
            Colors.TRANSPARENT_BLACK
        ).position(groundObject.x * tileScale, groundObject.y * tileScale)
            .registerBodyWithFixture(
                type = BodyType.STATIC,
                isSensor = true,
                world = world
            ).apply {
                body?.getFixtureList()?.filterData?.set(Filter().apply {
                    categoryBits = B2dParameters.FINISH_BIT
                    maskBits = B2dParameters.PLAYER_BIT
                })
                body?.getFixtureList()?.userData = Finish(1)
            }
    }

    fun buildTrap(
        trapObject: TiledMap.Object,
        worldLayers: WorldLayers,
        tileScale: Double = 1.0,
        world: World
    ): TrapComponent {
        val posX = (trapObject.x * tileScale) / world.customScale
        val posY = (trapObject.y * tileScale) / world.customScale
//            val bounds = (groundObject.bounds * TILE_SCALE) / world.customScale
        val bounds = trapObject.bounds * tileScale

        val body = world.createBody(BodyDef().apply {
            type = BodyType.STATIC
            position.set(posX, posY)
        })

        val boxWidth = bounds.width / world.customScale
        val boxHeight = bounds.height / world.customScale
        val trapUserData = TrapUserData()
        body.createFixture(FixtureDef().apply {
            // x и y должны быть 0, потому что рисуется относительно позиции
            // для объектов это верхний левый угол
            shape = BoxShape(boxWidth, boxHeight)
            isSensor = true
            filter = Filter().apply {
                categoryBits = B2dParameters.TRAP_BIT
                maskBits = B2dParameters.PLAYER_BIT
            }
            userData = trapUserData
        })

        // view
//        val view = worldLayers.ground.solidRect(trapObject.bounds.width, trapObject.bounds.height, color = Colors.GREEN)
//            .position(trapObject.x, trapObject.y)

//        view.body = body
//        body.view = view
        return TrapComponent(trapUserData, body)
    }

    fun buildCoin(
        coinObject: TiledMap.Object,
        worldLayers: WorldLayers,
        tileScale: Double = 1.0,
        world: World
    ): CoinComponent {
        val posX = (coinObject.x * tileScale) / world.customScale
        val posY = (coinObject.y * tileScale) / world.customScale

        val body = world.createBody(BodyDef().apply {
            type = BodyType.STATIC
            position.set(posX, posY)
        })

        val boxWidth = 32 / world.customScale
        val boxHeight = 32 / world.customScale

        val coinView = worldLayers.ground.sprite()
            .position(coinObject.x - 32, coinObject.y - 32)

        val cashType = coinObject.properties["type"]?.string?.let { CashTypes.valueOf(it) } ?: CashTypes.COIN

        val component = CoinComponent(cashType, body, coinView)

        body.createFixture(FixtureDef().apply {
            // x и y должны быть 0, потому что рисуется относительно позиции
            // для объектов это верхний левый угол
            shape = PolygonShape().apply {
                count = 4
                count = 4
                vertices[0].set(-(boxWidth / 2.0), -(boxHeight / 2.0))
                vertices[1].set(boxWidth / 2.0, -(boxHeight / 2.0))
                vertices[2].set(boxWidth / 2.0, boxHeight / 2.0)
                vertices[3].set(-(boxWidth / 2.0), boxHeight / 2.0)
                normals[0].set(0.0f, -1.0f)
                normals[1].set(1.0f, 0.0f)
                normals[2].set(0.0f, 1.0f)
                normals[3].set(-1.0f, 0.0f)
                centroid.setZero()
            }
            isSensor = true
            filter = Filter().apply {
                categoryBits = B2dParameters.COIN_BIT
                maskBits = B2dParameters.PLAYER_BIT
            }
            userData = component
        })

        return component
    }
}

data class WorldMetaData(
    val spawnArea: View,
    val finishArea: View,
//    val firstAbilitySelectionPoint: Point,
//    val secondAbilitySelectionPoint: Point,
    val traps: List<TrapComponent>,
    val coins: List<CoinComponent>
)

class Finish(
    private val countToFinish: Int
) {
    private val finishedPlayers = mutableSetOf<Long>()

    fun getFinishedPlayers(): Set<Long> = finishedPlayers

    fun playerFinished(playerId: Long) {
        if (finishedPlayers.size < countToFinish) {
            finishedPlayers.add(playerId)
        }
    }
}

class Water {

    fun applyToPlayer(player: PlayerPhysicState) {
        player.body.gravityScale = 0.25f
    }

    fun removeFromPlayer(player: PlayerPhysicState) {
        player.body.gravityScale = 1.0f
    }
}
